import type { Ref } from 'react'
import { type RefCallback } from 'react'

type PossibleRef<T> = Ref<T> | undefined

type RefCleanup<T> = ReturnType<RefCallback<T>>

const isCallbackRef = <T>(ref: PossibleRef<T>): ref is RefCallback<T> => {
  const tag = Object.prototype.toString.call(ref)
  return tag === '[object Function]' || tag === '[object AsyncFunction]'
}

function assignRef<T>(ref: PossibleRef<T>, value: T): RefCleanup<T> {
  if (isCallbackRef(ref)) {
    return ref(value)
  } else if (
    ref !== undefined &&
    ref !== null &&
    Object(ref) === ref &&
    !Array.isArray(ref) &&
    'current' in ref
  ) {
    // @ts-expect-error some hack
    ref.current = value
  }
}

export function mergeRefs<T>(...refs: PossibleRef<T>[]): RefCallback<T> {
  const cleanupMap = new Map<PossibleRef<T>, Exclude<RefCleanup<T>, void>>()

  return (node: T | null) => {
    refs.forEach(ref => {
      const cleanup = assignRef(ref, node)
      if (cleanup) {
        cleanupMap.set(ref, cleanup)
      }
    })

    if (cleanupMap.size > 0) {
      return () => {
        refs.forEach(ref => {
          const cleanup = cleanupMap.get(ref)
          if (cleanup) {
            cleanup()
          } else {
            assignRef(ref, null)
          }
        })
        cleanupMap.clear()
      }
    }
  }
}
